import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text/index';
import { NewShipFromContactModal } from 'ui-components/components/new-shipfrom-contact-modal';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { noop } from 'ui-components/default-props/noop';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';

import { useModal } from '../../../hooks/use-modal';
import { getShipFromContacts } from '../../carrier-pickup-api';
import { ManageShipFromContactModal } from '../../modal-components/shipfrom-contact-dropdown/manage-ship-from-contact-modal';
import { messages } from '../../modal-components/shipfrom-contact-dropdown/messages';
import { messages as pickupLocationMessages } from '../../modal-components/pickup-location/messages';

import style from './style.module.css';

/**
 * ShipFromContactDropdown component for selecting pickup contact
 * @param {object} props
 * @param {string} [props.className] - Additional CSS class name
 * @param {boolean} [props.hasError] - Whether the field has an error
 * @param {React.ReactNode} [props.inlineMessage] - Inline error message to display
 * @param {string} [props.inlineMessageType] - Type of inline message
 * @param {(contactId: number) => void} [props.onContactSelected] - Callback when contact is selected
 * @param {number|null} [props.selectedContactId] - Currently selected contact ID
 * @param {number} [props.shipFromId] - ID of the ship from location
 * @param {boolean} [props.showDefaultLocationNotes] - Whether to show default location notes
 * @returns {React.ReactElement} The ShipFromContactDropdown component
 */
export const ShipFromContactDropdown = ({
  className = undefined,
  hasError = false,
  inlineMessage,
  inlineMessageType = '',
  onContactSelected = noop,
  selectedContactId = null,
  shipFromId = 0,
  showDefaultLocationNotes = false,
}) => {
  const [contacts, setContacts] = useState([]);
  const { isOpen: isNewContactOpen, open: openNewContact, close: closeNewContact } = useModal();
  const { isOpen: isManageContactOpen, open: openManageContact, close: closeManageContact } = useModal();

  useEffect(() => {
    if (shipFromId) {
      getShipFromContacts(shipFromId)
        .then(({ data = [] }) => {
          setContacts(data);
          if (data.length === 1) {
            onContactSelected(data[0].shipFromContactId);
            return;
          }
          onContactSelected(null);
        })
        .catch(() => {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            title: <FormattedMessage {...pickupLocationMessages.errorNotificationTitle} />,
            content: <FormattedMessage {...pickupLocationMessages.errorNotificationMessage} />,
          });
        });
    }
  }, [onContactSelected, shipFromId]);

  const onContactCreateSuccess = useCallback(
    (contact) => {
      setContacts((prevContacts) => [...prevContacts, contact]);
      onContactSelected(contact.shipFromContactId);
      closeNewContact();
    },
    [onContactSelected, closeNewContact],
  );

  const handleContactEdited = useCallback((contactId, editedContact) => {
    setContacts((prevContacts) =>
      prevContacts.map((contact) => (contact.shipFromContactId === contactId ? { ...contact, ...editedContact } : contact)),
    );
  }, []);

  const handleContactRemoved = useCallback(
    (contactId) => {
      if (contactId === selectedContactId) {
        onContactSelected(null);
      }
      setContacts((prevContacts) => prevContacts.filter((contact) => contact.shipFromContactId !== contactId));
    },
    [onContactSelected, selectedContactId],
  );

  const selectedContact = contacts.find((contact) => contact.shipFromContactId === selectedContactId);
  const togglerText = useMemo(() => {
    if (selectedContact) {
      return `${selectedContact.firstName} ${selectedContact.lastName}`;
    }
    return contacts && contacts.length > 0 ? (
      <i>
        <FormattedMessage {...messages.selectContactDDToggleTitle} />
      </i>
    ) : (
      <i>
        <FormattedMessage {...messages.createContactDDToggleTitle} />
      </i>
    );
  }, [selectedContact, contacts]);

  return (
    <div>
      <LabelWithRequiredIndicator>
        <Text textType={TextType.Label} value={<FormattedMessage {...pickupLocationMessages.pickupLocationContactLabel} />} />
      </LabelWithRequiredIndicator>
      <DropdownMenu
        className={className}
        disabled={!shipFromId}
        dropdownMenuShouldHaveWidthOfButton
        fullWidth
        hasError={hasError}
        inlineMessage={inlineMessage}
        inlineMessageType={inlineMessageType}
        variant='form'
      >
        <DropdownMenuToggler>{togglerText}</DropdownMenuToggler>
        {contacts.map((contact) => (
          <DropdownMenuItem
            key={contact.shipFromContactId}
            id={contact.shipFromContactId}
            onClick={() => onContactSelected(contact.shipFromContactId)}
            selected={contact.shipFromContactId === selectedContactId}
          >
            <div>
              <Text textType={TextType.Caption} value={`${contact.firstName} ${contact.lastName}`} />
              <div className={style['item-subtitle']}>
                <Text textType={TextType.Caption} value={`${contact.phoneNumber} ${contact.phoneNumberExtension}`} />
                <Text textType={TextType.Caption} value={`${contact.emailAddress}`} />
              </div>
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={openNewContact}>
          <div>
            <Text textType={TextType.Caption} value={<FormattedMessage {...messages.createTitle} />} />
            <div className={style['item-subtitle']}>
              <Text textType={TextType.Caption} value={<FormattedMessage {...messages.createSubTitle} />} />
            </div>
          </div>
        </DropdownMenuItem>
      </DropdownMenu>

      <div className={style['contact-details-row']}>
        <div>
          {selectedContact && (
            <>
              <Text textType={TextType.Paragraph} value={`${selectedContact.phoneNumber} ${selectedContact.phoneNumberExtension}`} />
              <Text textType={TextType.Paragraph} value={selectedContact.emailAddress} />
            </>
          )}
        </div>
        <Button disabled={!shipFromId} onClick={openManageContact} theme='link'>
          <FormattedMessage {...messages.manageShipFromContactHeader} />
        </Button>
      </div>

      {isNewContactOpen && (
        <NewShipFromContactModal
          onCancel={closeNewContact}
          onClose={closeNewContact}
          onCreate={onContactCreateSuccess}
          shipFromId={shipFromId}
          showDefaultLocationNotes={showDefaultLocationNotes}
        />
      )}

      {isManageContactOpen && (
        <ManageShipFromContactModal
          contacts={contacts}
          onClose={closeManageContact}
          onCreate={onContactCreateSuccess}
          onEdit={handleContactEdited}
          onRemove={handleContactRemoved}
          shipFromId={shipFromId}
          showDefaultLocationNotes={showDefaultLocationNotes}
        />
      )}
    </div>
  );
};
